/**
 * IOSInstallHint — iOS Safari "Add to Home Screen" coaching card.
 *
 * iOS Safari never fires `beforeinstallprompt`, so we coach users through
 * the manual Share → Add to Home Screen flow.
 *
 * Behavior:
 *   - Visible only to engaged users (signed in + ≥1 real goal).
 *   - Hidden in Lovable preview and once launched as a PWA.
 *   - Shown on a "dopamine moment" (goal completed, certificate generated,
 *     streak milestone) or passively after 3 sessions.
 *   - Snooze 7 days per dismiss, lifetime cap 4 shows, separate
 *     "Don't show again" for permanent dismiss.
 *   - Streak-aware headline once streak ≥ 3.
 */
import { useEffect, useState } from "react";
import { Plus, X, Home, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useGoalQueries } from "@/hooks/useGoalQueries";
import { useIsStandalone } from "@/hooks/useIsStandalone";
import { isIOSSafari, isLovablePreviewHost } from "@/lib/platform";
import {
  isEligibleToPrompt,
  snoozeInstallPrompt,
  permanentlyDismissInstallPrompt,
} from "@/lib/installPromptState";
import { HomeScreenPreview } from "./HomeScreenPreview";
import { SafariShareDiagram } from "./SafariShareDiagram";

function isLandscapeOrIPad(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIPad = /iPad/i.test(ua) ||
    (/Macintosh/i.test(ua) && (navigator as any).maxTouchPoints > 1);
  const isLandscape = window.matchMedia?.("(orientation: landscape)").matches;
  return isIPad || !!isLandscape;
}

export function IOSInstallHint() {
  const { user } = useAuth();
  const { goals } = useGoalQueries();
  const isStandalone = useIsStandalone();

  const [armed, setArmed] = useState(false);
  const [, forceCheck] = useState(0);
  const [topPosition, setTopPosition] = useState(isLandscapeOrIPad());

  // Listen for "install moment" events to arm the prompt.
  useEffect(() => {
    const onArm = () => { setArmed(true); forceCheck((n) => n + 1); };
    window.addEventListener("install-prompt:arm", onArm);
    return () => window.removeEventListener("install-prompt:arm", onArm);
  }, []);

  // Re-evaluate share-icon position on orientation change.
  useEffect(() => {
    const update = () => setTopPosition(isLandscapeOrIPad());
    window.addEventListener("orientationchange", update);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("orientationchange", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const hasRealGoal = (goals || []).some((g: any) => !g?.isExample);
  const baseEligible =
    isIOSSafari() &&
    !isStandalone &&
    !isLovablePreviewHost() &&
    !!user &&
    hasRealGoal;

  if (!baseEligible) return null;
  if (!isEligibleToPrompt({ armed })) return null;

  const handleSnooze = () => {
    snoozeInstallPrompt();
    setArmed(false);
    forceCheck((n) => n + 1);
  };

  const handleDismissForever = () => {
    permanentlyDismissInstallPrompt();
    setArmed(false);
    forceCheck((n) => n + 1);
  };

  const headline = "Add GoalDigger to your Home Screen";

  const subheadline = "1-tap access, full-screen mode, and a +100 pt bonus.";

  return (
    <div
      role="dialog"
      aria-label="Add GoalDigger to Home Screen"
      className="fixed z-[60] left-3 right-3 md:left-auto md:right-6 md:w-[26rem] rounded-2xl border border-border bg-background shadow-2xl p-4 animate-in slide-in-from-bottom-4"
      style={{ bottom: "calc(5.5rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <button
        onClick={handleSnooze}
        aria-label="Snooze for 7 days"
        className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-muted text-muted-foreground"
        style={{ touchAction: "manipulation" }}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="h-10 w-10 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
          <Home className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-foreground">{headline}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{subheadline}</p>

          {/* Crucial clarification: the Share button lives in Safari, not in the app. */}
          <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-[11px] text-amber-900 dark:text-amber-100">
            <strong>Heads up:</strong> the <em>Share</em> button is in <strong>Safari itself</strong>,
            not inside this app — look at the address bar
            {topPosition ? " (top-right corner)." : " (bottom of the screen)."}
          </div>

          <SafariShareDiagram position={topPosition ? "top" : "bottom"} />

          <ol className="mt-3 space-y-2 text-xs text-foreground">
            <li className="flex items-start gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold flex-shrink-0">1</span>
              <span>
                Tap the <strong>Share</strong> icon (square with an up-arrow) at the
                {topPosition ? " top-right" : " bottom"} of Safari.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold flex-shrink-0">2</span>
              <span className="flex items-center gap-1 flex-wrap">
                Scroll and tap <Plus className="inline h-3.5 w-3.5" /> <strong>Add to Home Screen</strong>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold flex-shrink-0">3</span>
              <span>Tap <strong>Add</strong> — done!</span>
            </li>
          </ol>

          <HomeScreenPreview />

          <div className="mt-3 flex items-center gap-2 rounded-lg bg-orange-500/10 px-3 py-2">
            <Sparkles className="h-4 w-4 text-orange-600 flex-shrink-0" />
            <span className="text-[11px] font-semibold text-orange-900 dark:text-orange-100">
              You'll earn <strong>+100 points</strong> automatically the first time you open it from your home screen.
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSnooze}
              style={{ touchAction: "manipulation" }}
            >
              Remind me later
            </Button>
            <button
              type="button"
              onClick={handleDismissForever}
              className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
              style={{ touchAction: "manipulation" }}
            >
              Don't show again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
